import { useParams, useNavigate } from 'react-router-dom';
import {
  useGetRequestByIdQuery,
  useUpdateRequestMutation,
} from '../../services';
import {
  NavigateLinks,
  FormSelectInput,
  FormTextAreaInput,
  FormSubmitButton,
  LoaderBackdrop,
  Badge,
  TextInput,
} from '@/components';
import { useMemo, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { SelectInputOption } from '@/components/inputs/SelectInput';
import { useUserInfo } from '@/hooks';
import {
  useRequestActions,
  useRequestUtils,
} from '../../hooks';
import { requestsPageBreadcrumbs } from '../../config';

import styles from './ManageRequest.module.css';

interface ManageRequestForm {
  status: string;
  responseNote: string;
  assignedAssetId?: string;
}

const ManageRequest = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const userInfo = useUserInfo();

  const {
    data: request,
    isLoading,
    isError,
  } = useGetRequestByIdQuery({ requestId: id! }, { skip: !id });

  const [, { isLoading: isUpdating }] = useUpdateRequestMutation();

  const { control, handleSubmit, reset } = useForm<ManageRequestForm>({
    defaultValues: {
      status: '',
      responseNote: '',
      assignedAssetId: '',
    },
    mode: 'onChange',
  });

  const { onUpdateRequest } = useRequestActions();
  const { getStatusBadgeColors, getStatusLabel } = useRequestUtils();

  const breadcrumbs = useMemo(() => requestsPageBreadcrumbs(navigate), []);

  const statusOptions: SelectInputOption[] = [
    { value: 'PENDING', label: 'Pendiente' },
    { value: 'IN_PROGRESS', label: 'En progreso' },
    { value: 'ACCEPTED', label: 'Aceptado' },
    { value: 'REJECTED', label: 'Rechazado' },
  ];

  const statusMap: Record<string, string> = {
    PENDING: 'pending',
    IN_PROGRESS: 'in_process',
    ACCEPTED: 'accepted',
    REJECTED: 'rejected',
  };

  const onSubmit = async (data: ManageRequestForm) => {
    if (!request) return;
    const fullName = userInfo?.firstName && userInfo?.lastName 
      ? `${userInfo.firstName} ${userInfo.lastName}`.trim()
      : userInfo?.email || 'Usuario';
    
    await onUpdateRequest(id!, {
      ...request,
      status: statusMap[data.status],
      responseNote: data.responseNote,
      handledBy: fullName,
    });
  };

  useEffect(() => {
    if (request) {
      const statusKey =
        Object.keys(statusMap).find(
          (key) => statusMap[key] === request.status
        ) || '';
      reset({
        status: statusKey,
        responseNote: request.responseNote || '',
        assignedAssetId: request.assignedAssetId || '',
      });
    }
  }, [request, reset]);

  if (isLoading) {
    return <LoaderBackdrop />;
  }

  if (isError || !request) {
    return (
      <div className={styles.container}>
        <NavigateLinks actualPage='Error' breadcrumbs={breadcrumbs} />
        <div className={styles.content}>
          <div className={styles.header}>
            <h2 className={styles.title}>Error al cargar la solicitud</h2>
          </div>
        </div>
      </div>
    );
  }

  const statusColors = getStatusBadgeColors(request.status);

  return (
    <div className={styles.container}>
      <NavigateLinks
        actualPage='Gestionar Solicitud'
        breadcrumbs={breadcrumbs}
      />

      <div className={styles.content}>
        <div className={styles.header}>
          <h2 className={styles.title}>Gestionar Solicitud</h2>
          <div className='flex justify-end mt-2'>
            <Badge
              value={getStatusLabel(request.status)}
              backgroundColor={statusColors.backgroundColor}
              textColor={statusColors.textColor}
              showBall={false}
            />
          </div>
        </div>

        <div className={styles.grid}>
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Detalles de la Solicitud</h3>
            <div className='flex flex-col gap-4'>
              <TextInput
                name='adminName'
                placeholder='Administrador'
                value={request.adminFullName || 'N/A'}
                onChange={() => {}}
                disabled
              />
              <TextInput
                name='userName'
                placeholder='Usuario'
                value={request.userFullName || 'N/A'}
                onChange={() => {}}
                disabled
              />
              <TextInput
                name='requestType'
                placeholder='Tipo'
                value={request.assetType?.name || 'N/A'}
                onChange={() => {}}
                disabled
              />
              <TextInput
                name='neededDate'
                placeholder='Fecha necesaria'
                value={request.neededAt || 'No especificada'}
                onChange={() => {}}
                disabled
              />
              <TextInput
                name='requestNote'
                placeholder='Nota'
                value={request.note || 'Sin nota'}
                onChange={() => {}}
                disabled
              />
              <TextInput
                name='assignedAsset'
                placeholder='Equipo Asignado'
                value={
                  request.assignedAsset
                    ? `${request.assignedAsset.brand} ${request.assignedAsset.model}`
                    : 'Sin asignar'
                }
                onChange={() => {}}
                disabled
              />
            </div>
          </div>

          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Respuesta</h3>
            <form
              onSubmit={handleSubmit(onSubmit)}
              className={styles.responseForm}
            >
              <FormSelectInput
                name='status'
                placeholder='Seleccionar estado'
                control={control}
                options={statusOptions}
                required
              />
              <FormTextAreaInput
                name='responseNote'
                placeholder='Escribe tu respuesta aquí...'
                control={control}
                rows={4}
              />
              <FormSubmitButton
                action={() => handleSubmit(onSubmit)()}
                label='Guardar Respuesta'
                isLoading={isUpdating}
              />
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageRequest;
